<template>
  <div class="min-h-full bg-n-slate-1 p-6 lg:p-8">
    <!-- Page header -->
    <header class="mb-7">
      <div class="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div class="min-w-0">
          <div class="flex items-center gap-3">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-blue-9 text-white shadow-sm">
              <Icon icon="i-lucide-message-square" class="size-5" />
            </div>
            <div>
              <h1 class="text-xl font-semibold tracking-tight text-n-slate-12 sm:text-2xl">
                WhatsApp Templates
              </h1>
              <p class="mt-1 text-sm text-n-slate-11">
                Create and manage approved message templates for your WhatsApp campaigns.
              </p>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <!-- Sync Templates Button -->
          <button
            type="button"
            @click="refreshTemplates"
            :disabled="isRefreshing"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-n-weak bg-white px-4 text-sm font-medium text-n-slate-11 shadow-sm transition hover:bg-n-slate-1 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9/20 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-n-slate-2"
          >
            <Icon 
              :icon="isRefreshing ? 'i-lucide-loader-circle' : 'i-lucide-refresh-ccw'" 
              class="size-4" 
              :class="isRefreshing ? 'animate-spin' : ''"
            />
            {{ isRefreshing ? 'Syncing...' : 'Sync Templates' }}
          </button>

          <button
            type="button"
            @click="openCreateModal"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-blue-9 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-n-blue-10 focus:outline-none focus:ring-2 focus:ring-n-blue-9/30"
          >
            <Icon icon="i-lucide-plus" class="size-4" />
            New template
          </button>
        </div>
      </div>
    </header>

    <!-- Overview -->
    <section class="mb-7 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div class="kpi-card">
        <div class="kpi-icon bg-n-blue-1 text-n-blue-9">
          <Icon icon="i-lucide-files" class="size-4" />
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">Total</p>
          <p class="mt-0.5 text-xl font-semibold text-n-slate-12">{{ templates.length }}</p>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon bg-green-50 text-green-600">
          <Icon icon="i-lucide-circle-check" class="size-4" />
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">Approved</p>
          <p class="mt-0.5 text-xl font-semibold text-n-slate-12">{{ getApprovedCount }}</p>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon bg-yellow-50 text-yellow-600">
          <Icon icon="i-lucide-clock-3" class="size-4" />
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">Pending</p>
          <p class="mt-0.5 text-xl font-semibold text-n-slate-12">{{ getPendingCount }}</p>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon bg-red-50 text-red-600">
          <Icon icon="i-lucide-circle-x" class="size-4" />
        </div>
        <div class="min-w-0">
          <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">Rejected</p>
          <p class="mt-0.5 text-xl font-semibold text-n-slate-12">{{ getRejectedCount }}</p>
        </div>
      </div>
    </section>

    <Banner v-if="error" color="red" class="mb-5">
      <div class="flex items-start gap-2 text-start">
        <Icon icon="i-lucide-circle-alert" class="mt-0.5 size-4 shrink-0" />
        <span>{{ error }}</span>
      </div>
    </Banner>

    <!-- Loading -->
    <div v-if="isLoading" class="surface flex min-h-[360px] items-center justify-center">
      <div class="flex flex-col items-center gap-3 text-center">
        <SpinnerLoader :size="28" class="text-n-blue-9" />
        <p class="text-sm text-n-slate-11">Loading templates…</p>
      </div>
    </div>

    <!-- Template list -->
    <section v-else-if="templates.length" class="surface overflow-hidden">
      <div class="flex flex-col gap-3 border-b border-n-weak px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-sm font-semibold text-n-slate-12">Your templates</h2>
          <p class="mt-0.5 text-xs text-n-slate-10">Review status, content and available actions.</p>
        </div>
        <span class="inline-flex w-fit items-center rounded-md bg-n-slate-2 px-2.5 py-1 text-xs font-medium text-n-slate-11">
          {{ templates.length }} {{ templates.length === 1 ? 'template' : 'templates' }}
        </span>
      </div>

      <div class="divide-y divide-n-weak">
        <article
          v-for="template in templates"
          :key="template.id"
          class="template-row group"
        >
          <div class="flex min-w-0 flex-1 items-start gap-4">
            <div class="template-avatar">
              {{ template.name?.charAt(0)?.toUpperCase() || 'W' }}
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="truncate text-sm font-semibold text-n-slate-12" :title="template.name">
                  {{ template.name }}
                </h3>
                <span class="status-badge" :class="getStatusColor(template.status)">
                  <span class="size-1.5 rounded-full" :class="getStatusDotColor(template.status)"></span>
                  {{ template.status }}
                </span>
              </div>

              <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-10">
                <span class="inline-flex items-center gap-1">
                  <Icon icon="i-lucide-tag" class="size-3.5" />
                  {{ template.category || 'Uncategorized' }}
                </span>
                <span class="inline-flex items-center gap-1">
                  <Icon icon="i-lucide-globe-2" class="size-3.5" />
                  {{ template.language || 'en_US' }}
                </span>
                <span>{{ formatDate(template.created_at) }}</span>
              </div>

              <p class="mt-3 line-clamp-2 max-w-3xl text-sm leading-6 text-n-slate-11">
                {{ getTemplateBody(template) || 'No message body available.' }}
              </p>

              <div v-if="getTemplateHeader(template)" class="mt-2">
                <span class="text-[10px] font-medium text-n-slate-10">Header:</span>
                <span class="text-xs text-n-slate-11">{{ getTemplateHeader(template).text || getTemplateHeader(template).format }}</span>
              </div>

              <div v-if="getTemplateFooter(template)" class="mt-1">
                <span class="text-[10px] font-medium text-n-slate-10">Footer:</span>
                <span class="text-xs text-n-slate-11">{{ getTemplateFooter(template).text }}</span>
              </div>

              <div v-if="getTemplateButtons(template)" class="mt-2 flex flex-wrap items-center gap-1.5">
                <span class="text-[10px] font-medium text-n-slate-10">Buttons:</span>
                <span
                  v-for="button in getTemplateButtons(template).buttons"
                  :key="button.text"
                  class="inline-flex items-center rounded-md border border-n-weak px-2 py-0.5 text-[10px] text-n-slate-11"
                >
                  {{ button.text }}
                </span>
              </div>
            </div>
          </div>

          <div class="mt-4 flex shrink-0 items-center gap-1 sm:mt-0 sm:pl-4">
            <button type="button" @click="openPreviewModal(template)" class="row-action" title="Preview">
              <Icon icon="i-lucide-eye" class="size-4" />
              <span class="sr-only">Preview</span>
            </button>
            <button type="button" @click="openEditModal(template)" class="row-action" title="Edit">
              <Icon icon="i-lucide-pencil" class="size-4" />
              <span class="sr-only">Edit</span>
            </button>
            <button type="button" @click="deleteTemplate(template)" class="row-action row-action-danger" title="Delete">
              <Icon icon="i-lucide-trash-2" class="size-4" />
              <span class="sr-only">Delete</span>
            </button>
          </div>
        </article>
      </div>
    </section>

    <!-- Empty state -->
    <section v-else class="surface flex min-h-[430px] items-center justify-center px-6 py-12 text-center">
      <div class="max-w-md">
        <div class="mx-auto flex size-14 items-center justify-center rounded-2xl bg-n-blue-1 text-n-blue-9">
          <Icon icon="i-lucide-message-square-plus" class="size-7" />
        </div>
        <h2 class="mt-5 text-lg font-semibold text-n-slate-12">Create your first template</h2>
        <p class="mx-auto mt-2 max-w-sm text-sm leading-6 text-n-slate-11">
          Build a reusable WhatsApp message, preview exactly how it will look, and submit it for approval.
        </p>
        <button
          type="button"
          @click="openCreateModal"
          class="mt-6 inline-flex h-10 items-center gap-2 rounded-lg bg-n-blue-9 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-n-blue-10 focus:outline-none focus:ring-2 focus:ring-n-blue-9/30"
        >
          <Icon icon="i-lucide-plus" class="size-4" />
          Create template
        </button>
      </div>
    </section>

    <!-- Create / edit modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-screen items-center justify-center p-3 sm:p-5">
        <div class="modal-backdrop"></div>

        <div ref="formContainer" class="relative flex h-[min(900px,94vh)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-white shadow-2xl dark:bg-n-slate-2">
          <!-- Modal top bar -->
          <div class="border-b border-n-weak bg-white px-5 py-4 dark:bg-n-slate-2 sm:px-6">
            <div class="flex items-center justify-between gap-4">
              <div class="flex min-w-0 items-center gap-3">
                <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-blue-1 text-n-blue-9">
                  <Icon icon="i-lucide-message-square-text" class="size-4.5" />
                </div>
                <div class="min-w-0">
                  <h2 class="truncate text-base font-semibold text-n-slate-12">
                    {{ isEditing ? 'Edit template' : 'Create WhatsApp template' }}
                  </h2>
                  <p class="mt-0.5 text-xs text-n-slate-10">
                    {{ isEditing ? 'Update your template details and content.' : 'Set up your message in a few simple steps.' }}
                  </p>
                </div>
              </div>
              <button type="button" @click="closeModal" class="icon-button" title="Close">
                <Icon icon="i-lucide-x" class="size-4.5" />
                <span class="sr-only">Close</span>
              </button>
            </div>

            <!-- Progress -->
            <div class="mt-5 grid grid-cols-4 gap-2">
              <button
                v-for="(title, index) in stepTitles"
                :key="title"
                type="button"
                @click="index + 1 < currentStep ? currentStep = index + 1 : null"
                class="step-item"
                :class="{
                  'step-item-active': index + 1 === currentStep,
                  'step-item-complete': index + 1 < currentStep,
                }"
              >
                <span class="step-number">
                  <Icon v-if="index + 1 < currentStep" icon="i-lucide-check" class="size-3.5" />
                  <span v-else>{{ index + 1 }}</span>
                </span>
                <span class="hidden text-xs font-medium sm:block">{{ title }}</span>
              </button>
            </div>
          </div>

          <!-- Modal body -->
          <div class="flex min-h-0 flex-1 flex-col lg:flex-row">
            <!-- Form -->
            <div class="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
              <!-- STEP 1 -->
              <div v-show="currentStep === 1" class="form-step">
                <div class="mb-6">
                  <p class="section-eyebrow">Step 1 of 4</p>
                  <h3 class="section-title">Template details</h3>
                  <p class="section-description">Choose how this message will be used and give it a recognizable name.</p>
                </div>

                <div class="field-group">
                  <label class="field-label">Template category <span class="required">*</span></label>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <button
                      v-for="category in templateCategories"
                      :key="category.value"
                      type="button"
                      @click="selectCategory(category.value)"
                      class="category-card"
                      :class="form.category === category.value ? 'category-card-selected' : ''"
                    >
                      <span class="category-card-icon">
                        <Icon
                          :icon="category.value === 'MARKETING' ? 'i-lucide-megaphone' : category.value === 'UTILITY' ? 'i-lucide-receipt-text' : 'i-lucide-shield-check'"
                          class="size-4"
                        />
                      </span>
                      <span class="min-w-0 text-left">
                        <span class="block text-sm font-semibold text-n-slate-12">{{ category.label }}</span>
                        <span class="mt-0.5 block text-xs leading-5 text-n-slate-10">{{ category.description }}</span>
                      </span>
                      <span v-if="form.category === category.value" class="ml-auto text-n-blue-9">
                        <Icon icon="i-lucide-circle-check" class="size-4" />
                      </span>
                    </button>
                  </div>
                  <p v-if="!form.category && showStepErrors" class="field-error">
                    <Icon icon="i-lucide-circle-alert" class="size-3.5" /> Select a template category.
                  </p>
                </div>

                <div class="field-group">
                  <label class="field-label" for="template-name">Template name <span class="required">*</span></label>
                  <div class="relative">
                    <input
                      id="template-name"
                      v-model="form.name"
                      type="text"
                      maxlength="64"
                      class="form-input pr-16"
                      :class="!form.name && showStepErrors ? 'form-input-error' : ''"
                      placeholder="e.g. order_confirmation"
                      @input="validateStep(1)"
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-n-slate-10">{{ form.name.length }}/64</span>
                  </div>
                  <p class="field-help">Use lowercase letters, numbers and underscores where possible.</p>
                  <p v-if="!form.name && showStepErrors" class="field-error">
                    <Icon icon="i-lucide-circle-alert" class="size-3.5" /> Template name is required.
                  </p>
                </div>

                <div v-if="form.category === 'AUTHENTICATION'" class="notice-box notice-box-warning">
                  <div class="notice-icon"><Icon icon="i-lucide-shield-check" class="size-4" /></div>
                  <div class="min-w-0 flex-1">
                    <h4 class="text-sm font-semibold text-n-slate-12">Authentication settings</h4>
                    <p class="mt-1 text-xs leading-5 text-n-slate-11">Configure how long verification codes remain valid and how customers copy them.</p>
                    <div class="mt-4 grid gap-3 sm:grid-cols-2">
                      <div>
                        <label class="field-label-small">Message TTL (seconds)</label>
                        <input v-model.number="form.messageTtl" type="number" min="1" class="form-input" placeholder="60" />
                      </div>
                      <div>
                        <label class="field-label-small">Code expiration (minutes)</label>
                        <input v-model.number="form.codeExpiration" type="number" min="1" class="form-input" placeholder="5" />
                      </div>
                      <div class="sm:col-span-2">
                        <label class="field-label-small">Copy code button text</label>
                        <input v-model="form.copyCodeButtonText" type="text" class="form-input" placeholder="Tap to copy" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- STEP 2 -->
              <div v-show="currentStep === 2" class="form-step">
                <div class="mb-6">
                  <p class="section-eyebrow">Step 2 of 4</p>
                  <h3 class="section-title">Header</h3>
                  <p class="section-description">Add optional media or a short heading before the message body.</p>
                </div>

                <div v-if="form.category !== 'AUTHENTICATION'" class="field-group">
                  <label class="field-label">Header type</label>
                  <div class="grid gap-2 sm:grid-cols-5">
                    <button
                      v-for="option in [
                        { value: '', label: 'None', icon: 'i-lucide-minus' },
                        { value: 'text', label: 'Text', icon: 'i-lucide-type' },
                        { value: 'image', label: 'Image', icon: 'i-lucide-image' },
                        { value: 'document', label: 'Document', icon: 'i-lucide-file-text' },
                        { value: 'video', label: 'Video', icon: 'i-lucide-video' },
                      ]"
                      :key="option.value || 'none'"
                      type="button"
                      @click="form.headerOption = option.value; clearHeaderPreview(); clearHeaderVariables()"
                      class="option-button"
                      :class="form.headerOption === option.value ? 'option-button-selected' : ''"
                    >
                      <Icon :icon="option.icon" class="size-4" />
                      <span>{{ option.label }}</span>
                    </button>
                  </div>
                </div>

                <div v-if="form.category !== 'AUTHENTICATION' && form.headerOption === 'text'" class="field-group">
                  <label class="field-label" for="header-text">Header text</label>
                  
                  <!-- Header variable insertion buttons -->
                  <div class="mb-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-n-weak bg-n-slate-1 p-2">
                    <span class="mr-1 text-[11px] font-medium text-n-slate-10">Insert variable</span>
                    <button
                      v-for="variable in headerSuggestedVariables"
                      :key="variable"
                      type="button"
                      @click="insertHeaderVariable(variable)"
                      class="variable-button"
                      :disabled="form.headerVariables.length >= 1"
                    >
                      {{ variable }}
                    </button>
                    <button 
                      type="button" 
                      @click="addCustomHeaderVariable" 
                      class="variable-button variable-button-add"
                      :disabled="form.headerVariables.length >= 1"
                    >
                      <Icon icon="i-lucide-plus" class="size-3" /> Custom
                    </button>
                    <span v-if="form.headerVariables.length >= 1" class="text-[10px] text-amber-600 ml-2">
                      <Icon icon="i-lucide-info" class="size-3 inline" /> Max 1 variable allowed
                    </span>
                  </div>

                  <input 
                    id="header-text" 
                    v-model="form.headerText" 
                    type="text" 
                    class="form-input" 
                    placeholder="Your order is confirmed"
                    @input="updateHeaderVariables()"
                  />
                  <p class="field-help">Keep the header concise so the message remains easy to scan. Maximum 1 variable allowed.</p>
                  
                  <!-- Header Variables -->
                  <div v-if="form.headerVariables.length" class="mt-3">
                    <div class="mb-2 flex items-center justify-between">
                      <label class="field-label-small mb-0">Header variable example</label>
                      <span class="text-[11px] text-n-slate-10">Used in the live preview</span>
                    </div>
                    <div class="overflow-hidden rounded-xl border border-n-weak">
                      <div v-for="variable in form.headerVariables" :key="variable" class="flex items-center gap-3 p-3">
                        <span class="variable-chip shrink-0">{{ variable }}</span>
                        <input
                          v-model="form.headerVariableSamples[variable]"
                          type="text"
                          class="form-input h-9 flex-1"
                          placeholder="Enter an example value"
                        />
                        <button 
                          type="button" 
                          @click="removeHeaderVariable(variable)"
                          class="text-n-slate-10 hover:text-red-600 transition"
                          title="Remove variable"
                        >
                          <Icon icon="i-lucide-x" class="size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="form.category !== 'AUTHENTICATION' && ['image', 'document', 'video'].includes(form.headerOption)" class="field-group">
                  <label class="field-label">Upload {{ form.headerOption }}</label>
                  <label class="upload-zone">
                    <input
                      type="file"
                      @change="handleFileUpload"
                      class="sr-only"
                      :accept="form.headerOption === 'image' ? 'image/*' : form.headerOption === 'video' ? 'video/*' : '.pdf,.doc,.docx'"
                    />
                    <span class="flex size-10 items-center justify-center rounded-xl bg-n-slate-2 text-n-slate-10">
                      <Icon :icon="form.headerOption === 'image' ? 'i-lucide-image-plus' : form.headerOption === 'video' ? 'i-lucide-video' : 'i-lucide-file-plus-2'" class="size-5" />
                    </span>
                    <span class="min-w-0">
                      <span class="block text-sm font-medium text-n-slate-12">{{ form.headerFile ? form.headerFile.name : 'Choose a file' }}</span>
                      <span class="mt-0.5 block text-xs text-n-slate-10">Click to browse from your device</span>
                    </span>
                    <span class="ml-auto text-xs font-medium text-n-blue-9">Browse</span>
                  </label>
                  <div v-if="form.headerPreview && form.headerOption === 'image'" class="mt-3 overflow-hidden rounded-xl border border-n-weak bg-n-slate-1 p-2">
                    <img :src="form.headerPreview" alt="Header preview" class="max-h-48 w-full rounded-lg object-cover" />
                  </div>
                </div>

                <div v-if="form.category === 'AUTHENTICATION'" class="notice-box">
                  <div class="notice-icon"><Icon icon="i-lucide-info" class="size-4" /></div>
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">Authentication templates don't use headers.</p>
                    <p class="mt-1 text-xs leading-5 text-n-slate-10">Continue to the message step to configure the verification content.</p>
                  </div>
                </div>
              </div>

              <!-- STEP 3 -->
              <div v-show="currentStep === 3" class="form-step">
                <div class="mb-6">
                  <p class="section-eyebrow">Step 3 of 4</p>
                  <h3 class="section-title">Message content</h3>
                  <p class="section-description">Write the message customers will receive. Add variables where dynamic values are needed.</p>
                </div>

                <div class="field-group">
                  <div class="mb-2 flex items-center justify-between gap-3">
                    <label class="field-label mb-0">Message body <span class="required">*</span></label>
                    <span class="text-[11px] text-n-slate-10">{{ form.body.length }}/1024</span>
                  </div>

                  <div class="mb-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-n-weak bg-n-slate-1 p-2">
                    <span class="mr-1 text-[11px] font-medium text-n-slate-10">Insert variable</span>
                    <button
                      v-for="variable in bodySuggestedVariables"
                      :key="variable"
                      type="button"
                      @click="insertBodyVariable(variable)"
                      class="variable-button"
                    >
                      {{ variable }}
                    </button>
                    <button type="button" @click="addCustomBodyVariable" class="variable-button variable-button-add">
                      <Icon icon="i-lucide-plus" class="size-3" /> Custom
                    </button>
                  </div>

                  <textarea
                    ref="bodyTextarea"
                    v-model="form.body"
                    rows="8"
                    maxlength="1024"
                    class="form-textarea font-sans"
                    :class="!form.body && showStepErrors ? 'form-input-error' : ''"
                    placeholder="Hi {{customer_name}}, your order {{order_number}} has been confirmed."
                    @input="validateStep(3); updateBodyVariables()"
                  ></textarea>

                  <p v-if="!form.body && showStepErrors" class="field-error">
                    <Icon icon="i-lucide-circle-alert" class="size-3.5" /> Message body is required.
                  </p>
                </div>

                <!-- Body Variables -->
                <div v-if="form.bodyVariables.length" class="field-group">
                  <div class="mb-2 flex items-center justify-between">
                    <label class="field-label mb-0">Variable examples</label>
                    <span class="text-[11px] text-n-slate-10">Used in the live preview</span>
                  </div>
                  <div class="overflow-hidden rounded-xl border border-n-weak">
                    <div v-for="variable in form.bodyVariables" :key="variable" class="flex items-center gap-3 border-b border-n-weak p-3 last:border-b-0">
                      <span class="variable-chip shrink-0">{{ variable }}</span>
                      <input
                        v-model="form.bodyVariableSamples[variable]"
                        type="text"
                        class="form-input h-9 flex-1"
                        placeholder="Enter an example value"
                      />
                      <button 
                        type="button" 
                        @click="removeBodyVariable(variable)"
                        class="text-n-slate-10 hover:text-red-600 transition"
                        title="Remove variable"
                      >
                        <Icon icon="i-lucide-x" class="size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- STEP 4 -->
              <div v-show="currentStep === 4" class="form-step">
                <div class="mb-6">
                  <p class="section-eyebrow">Step 4 of 4</p>
                  <h3 class="section-title">Actions & details</h3>
                  <p class="section-description">Finish the template with optional footer text, customer actions and account details.</p>
                </div>

                <div class="field-group">
                  <label class="field-label" for="footer-text">Footer <span class="optional">Optional</span></label>
                  <input id="footer-text" v-model="form.footer" type="text" class="form-input" placeholder="Reply STOP to unsubscribe" />
                </div>

                <div v-if="form.category !== 'AUTHENTICATION'" class="field-group">
                  <div class="mb-2 flex items-center justify-between">
                    <label class="field-label mb-0">Buttons <span class="optional">Optional</span></label>
                    <span class="text-[11px] text-n-slate-10">Add up to the supported actions</span>
                  </div>

                  <div class="space-y-3">
                    <div v-if="form.buttons.website" class="action-card">
                      <div class="action-card-header">
                        <div class="flex items-center gap-2">
                          <span class="action-icon bg-n-blue-1 text-n-blue-9"><Icon icon="i-lucide-globe-2" class="size-4" /></span>
                          <div>
                            <p class="text-sm font-medium text-n-slate-12">Website button</p>
                            <p class="text-xs text-n-slate-10">Send customers to a web page</p>
                          </div>
                        </div>
                        <button type="button" @click="form.buttons.website = null" class="text-xs font-medium text-red-600 hover:underline">Remove</button>
                      </div>
                      <div class="grid gap-3 sm:grid-cols-2">
                        <input v-model="form.buttons.website.text" type="text" class="form-input" placeholder="Button text" />
                        <input v-model="form.buttons.website.url" type="url" class="form-input" placeholder="https://example.com" />
                      </div>
                    </div>

                    <div v-if="form.buttons.call" class="action-card">
                      <div class="action-card-header">
                        <div class="flex items-center gap-2">
                          <span class="action-icon bg-green-50 text-green-600"><Icon icon="i-lucide-phone" class="size-4" /></span>
                          <div>
                            <p class="text-sm font-medium text-n-slate-12">Call button</p>
                            <p class="text-xs text-n-slate-10">Let customers call a number</p>
                          </div>
                        </div>
                        <button type="button" @click="form.buttons.call = null" class="text-xs font-medium text-red-600 hover:underline">Remove</button>
                      </div>
                      <div class="grid gap-3 sm:grid-cols-2">
                        <input v-model="form.buttons.call.phone" type="text" class="form-input" placeholder="Phone number" />
                        <input v-model="form.buttons.call.country" type="text" class="form-input" placeholder="KE" />
                      </div>
                    </div>

                    <div v-if="form.buttons.copyCode" class="action-card">
                      <div class="action-card-header">
                        <div class="flex items-center gap-2">
                          <span class="action-icon bg-purple-50 text-purple-600"><Icon icon="i-lucide-copy" class="size-4" /></span>
                          <div>
                            <p class="text-sm font-medium text-n-slate-12">Copy code button</p>
                            <p class="text-xs text-n-slate-10">Give customers a one-tap copy action</p>
                          </div>
                        </div>
                        <button type="button" @click="form.buttons.copyCode = null" class="text-xs font-medium text-red-600 hover:underline">Remove</button>
                      </div>
                      <input v-model="form.buttons.copyCode.code" type="text" class="form-input" placeholder="Code to copy" />
                    </div>
                  </div>

                  <div class="mt-3 flex flex-wrap gap-2">
                    <button v-if="!form.buttons.website" type="button" @click="form.buttons.website = { text: 'Visit Website', url: '' }" class="secondary-action"><Icon icon="i-lucide-plus" class="size-3.5" /> Website</button>
                    <button v-if="!form.buttons.call" type="button" @click="form.buttons.call = { phone: '', country: 'KE' }" class="secondary-action"><Icon icon="i-lucide-plus" class="size-3.5" /> Call</button>
                    <button v-if="!form.buttons.copyCode" type="button" @click="form.buttons.copyCode = { code: '' }" class="secondary-action"><Icon icon="i-lucide-plus" class="size-3.5" /> Copy code</button>
                  </div>
                </div>

                <div class="field-group">
                  <label class="field-label" for="waba-id">WhatsApp Business Account ID</label>
                  <input id="waba-id" v-model="form.wabaId" type="number" class="form-input" placeholder="225592063964056" />
                  <p class="field-help">Leave this blank to use the configured default account.</p>
                </div>
              </div>

              <!-- Navigation -->
              <div class="sticky bottom-0 mt-8 flex items-center justify-between border-t border-n-weak bg-white pt-4 dark:bg-n-slate-2">
                <button type="button" @click="closeModal" class="secondary-button">
                  Close
                </button>
                <div class="flex items-center gap-2">
                  <button v-if="currentStep > 1" type="button" @click="prevStep" class="secondary-button">
                    <Icon icon="i-lucide-arrow-left" class="size-4" /> Back
                  </button>
                  <span v-else></span>
                  <button v-if="currentStep < totalSteps" type="button" @click="nextStep" class="primary-button">
                    Continue <Icon icon="i-lucide-arrow-right" class="size-4" />
                  </button>
                  <button v-else type="button" @click="saveTemplate" :disabled="isSubmitting || !isFormValid" class="primary-button">
                    <Icon :icon="isSubmitting ? 'i-lucide-loader-circle' : isEditing ? 'i-lucide-save' : 'i-lucide-check'" class="size-4" :class="isSubmitting ? 'animate-spin' : ''" />
                    {{ isSubmitting ? 'Submitting…' : (isEditing ? 'Submit changes' : 'Submit template') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Preview -->
            <aside class="w-full shrink-0 border-t border-n-weak bg-n-slate-1 p-5 lg:w-[380px] lg:border-l lg:border-t-0 sm:p-6">
              <div class="sticky top-0">
                <div class="mb-4 flex items-center justify-between">
                  <div>
                    <p class="section-eyebrow">Preview</p>
                    <h3 class="mt-1 text-sm font-semibold text-n-slate-12">Customer view</h3>
                  </div>
                  <span class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-white px-2 py-1 text-[10px] font-medium text-n-slate-10 dark:bg-n-slate-2">
                    <span class="size-1.5 rounded-full bg-green-600"></span> Live
                  </span>
                </div>

                <div class="mx-auto max-w-[330px] overflow-hidden rounded-2xl border border-n-weak bg-[#efeae2] shadow-sm">
                  <div class="flex items-center gap-3 bg-[#075e54] px-4 py-3 text-white">
                    <div class="flex size-8 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">
                      {{ form.name?.charAt(0)?.toUpperCase() || 'W' }}
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-xs font-semibold">{{ form.name || 'Template name' }}</p>
                      <p class="text-[10px] text-white/70">WhatsApp Business</p>
                    </div>
                    <Icon icon="i-lucide-more-vertical" class="size-4" />
                  </div>

                  <div class="preview-chat min-h-[420px] space-y-2 p-3">
                    <div class="text-center text-[9px] font-medium text-slate-500">TODAY</div>
                    <div class="max-w-[92%] rounded-lg rounded-tl-sm bg-white p-2.5 shadow-sm">
                      <div v-if="form.headerOption === 'text' && form.headerText" class="mb-2 text-xs font-semibold text-slate-800">{{ renderPreviewHeader() }}</div>
                      <div v-if="form.headerOption === 'image' && form.headerPreview" class="mb-2 overflow-hidden rounded-md">
                        <img :src="form.headerPreview" alt="Preview" class="max-h-36 w-full object-cover" />
                      </div>
                      <div v-if="['document', 'video'].includes(form.headerOption) && form.headerFile" class="mb-2 flex items-center gap-2 rounded-md bg-slate-100 p-2 text-[10px] text-slate-600">
                        <Icon :icon="form.headerOption === 'video' ? 'i-lucide-video' : 'i-lucide-file-text'" class="size-4" />
                        <span class="truncate">{{ form.headerFile.name }}</span>
                      </div>
                      <p class="whitespace-pre-wrap text-xs leading-5 text-slate-800">{{ renderPreviewBody() || 'Your message will appear here…' }}</p>
                      <p v-if="form.footer" class="mt-2 text-[10px] text-slate-500">{{ form.footer }}</p>
                      <div v-if="form.buttons.website || form.buttons.call || form.buttons.copyCode" class="mt-2 border-t border-slate-100 pt-2">
                        <div v-if="form.buttons.website" class="py-1.5 text-center text-[10px] font-medium text-[#128c7e]">{{ form.buttons.website.text || 'Visit Website' }}</div>
                        <div v-if="form.buttons.call" class="border-t border-slate-100 py-1.5 text-center text-[10px] font-medium text-[#128c7e]">{{ form.buttons.call.phone || 'Call' }}</div>
                        <div v-if="form.buttons.copyCode" class="border-t border-slate-100 py-1.5 text-center text-[10px] font-medium text-[#128c7e]">{{ form.buttons.copyCode.code || 'Copy Code' }}</div>
                      </div>
                      <p class="mt-1 text-right text-[9px] text-slate-400">Now</p>
                    </div>
                  </div>
                </div>

                <div v-if="form.bodyVariables.length || form.headerVariables.length" class="mx-auto mt-4 max-w-[330px] rounded-xl border border-n-weak bg-white p-3 dark:bg-n-slate-2">
                  <p class="text-[11px] font-semibold text-n-slate-12">Preview values</p>
                  <div class="mt-2 space-y-1.5">
                    <div v-for="variable in form.bodyVariables" :key="variable" class="flex items-center justify-between gap-3 text-[10px]">
                      <span class="font-mono text-n-blue-9">{{ variable }}</span>
                      <span class="truncate text-n-slate-10">{{ form.bodyVariableSamples[variable] || 'Sample value' }}</span>
                    </div>
                    <div v-for="variable in form.headerVariables" :key="variable" class="flex items-center justify-between gap-3 text-[10px]">
                      <span class="font-mono text-n-blue-9">{{ variable }}</span>
                      <span class="truncate text-n-slate-10">{{ form.headerVariableSamples[variable] || 'Sample value' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>

    <!-- Standalone preview -->
    <div v-if="showPreview" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex min-h-screen items-center justify-center p-4">
        <div class="modal-backdrop"></div>
        <div class="relative w-full max-w-md overflow-hidden rounded-2xl border border-n-weak bg-white shadow-2xl dark:bg-n-slate-2">
          <div class="flex items-center justify-between border-b border-n-weak px-5 py-4">
            <div>
              <p class="section-eyebrow">Preview</p>
              <h3 class="mt-1 text-base font-semibold text-n-slate-12">{{ previewData?.name || 'Template preview' }}</h3>
            </div>
            <button type="button" @click="closePreview" class="icon-button"><Icon icon="i-lucide-x" class="size-4.5" /></button>
          </div>
          <div class="bg-n-slate-1 p-6">
            <div class="mx-auto max-w-[330px] overflow-hidden rounded-2xl border border-n-weak bg-[#efeae2] shadow-sm">
              <div class="flex items-center gap-3 bg-[#075e54] px-4 py-3 text-white">
                <div class="flex size-8 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">W</div>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-xs font-semibold">{{ previewData?.name || 'Template' }}</p>
                  <p class="text-[10px] text-white/70">WhatsApp Business</p>
                </div>
              </div>
              <div class="preview-chat p-3">
                <div class="max-w-[92%] rounded-lg rounded-tl-sm bg-white p-3 shadow-sm">
                  <p class="whitespace-pre-wrap text-xs leading-5 text-slate-800">{{ getTemplateBody(previewData) || 'No body text' }}</p>
                  <div v-if="getTemplateButtons(previewData)" class="mt-2 border-t border-slate-100 pt-2">
                    <div v-for="button in getTemplateButtons(previewData).buttons" :key="button.text" class="border-b border-slate-100 py-1.5 text-center text-[10px] font-medium text-[#128c7e] last:border-b-0">{{ button.text }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end border-t border-n-weak px-5 py-3">
            <button type="button" @click="closePreview" class="secondary-button">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineOptions({
  name: 'WhatsAppTemplates',
});

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();

// --- Template & Element Refs ---
const bodyTextarea = ref(null);
const formContainer = ref(null);

// --- State ---
const isRefreshing = ref(false);
const error = ref(null);

const showModal = ref(false);
const showPreview = ref(false);
const isEditing = ref(false);
const isSubmitting = ref(false);
const editingId = ref(null);

// Wizard State
const currentStep = ref(1);
const totalSteps = ref(4);
const showStepErrors = ref(false);
const stepTitles = ref(['Details', 'Header', 'Message', 'Actions']);

const templateCategories = ref([
  { value: 'MARKETING', label: 'Marketing', description: 'Promotional content' },
  { value: 'UTILITY', label: 'Utility', description: 'Transactional updates' },
  { value: 'AUTHENTICATION', label: 'Authentication', description: 'Login & verification' },
]);

// Separate suggested variables for body and header
const bodySuggestedVariables = ref([
  'customer_name',
  'order_number',
  'amount',
  'date',
  'time',
  'code',
  'product_name',
]);

const headerSuggestedVariables = ref([
  'header_title',
  'order_id',
  'user_name',
  'product',
]);

const form = reactive({
  category: '',
  name: '',
  headerOption: '',
  headerText: '',
  headerFile: null,
  headerPreview: null,
  body: '',
  // Body variables
  bodyVariables: [],
  bodyVariableSamples: {},
  // Header variables (max 1)
  headerVariables: [],
  headerVariableSamples: {},
  footer: '',
  buttons: {
    website: null,
    call: null,
    copyCode: null,
  },
  wabaId: '',
  messageTtl: 60,
  codeExpiration: 5,
  copyCodeButtonText: 'Tap to copy',
});

const previewData = ref(null);

// ============ TEMPLATES FROM INBOX PROPS ============
const templates = computed(() => {
  return props.inbox?.message_templates || [];
});

const isLoading = computed(() => {
  return !props.inbox?.message_templates;
});

// ============ CHATWOOT'S HELPER FUNCTIONS ============
const getTemplateBody = template => {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
};

const getTemplateHeader = template => {
  return findComponentByType(template, COMPONENT_TYPES.HEADER);
};

const getTemplateFooter = template => {
  return findComponentByType(template, COMPONENT_TYPES.FOOTER);
};

const getTemplateButtons = template => {
  return findComponentByType(template, COMPONENT_TYPES.BUTTONS);
};

const hasMediaContent = template => {
  const header = getTemplateHeader(template);
  return header && MEDIA_FORMATS.includes(header.format);
};

// ============ SYNC TEMPLATES ============
const refreshTemplates = async () => {
  if (!props.inbox?.id) {
    useAlert('No inbox selected');
    return;
  }

  isRefreshing.value = true;
  
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    useAlert('Templates synced successfully!');
  } catch (error) {
    console.error('Error syncing templates:', error);
    useAlert('Failed to sync templates. Please try again.');
  } finally {
    isRefreshing.value = false;
  }
};

// ============ WIZARD NAVIGATION ============
const scrollToFormTop = () => {
  if (formContainer.value) {
    formContainer.value.scrollTop = 0;
  } else {
    const formEl = document.querySelector('.overflow-y-auto');
    if (formEl) formEl.scrollTop = 0;
  }
};

const nextStep = () => {
  showStepErrors.value = true;

  if (currentStep.value === 1) {
    if (!form.category || !form.name) {
      useAlert('Please select a template type and enter a name');
      return;
    }
    if (form.category === 'AUTHENTICATION' && !form.copyCodeButtonText) {
      useAlert('Please enter the copy code button text');
      return;
    }
  }

  if (currentStep.value === 3) {
    if (!form.body) {
      useAlert('Please enter a message body');
      return;
    }
  }

  if (currentStep.value < totalSteps.value) {
    currentStep.value++;
    showStepErrors.value = false;
    scrollToFormTop();
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
    showStepErrors.value = false;
    scrollToFormTop();
  }
};

const validateStep = (step) => {
  if (step === 1 && form.name) {
    showStepErrors.value = false;
  }
  if (step === 3 && form.body) {
    showStepErrors.value = false;
  }
};

// ============ CATEGORY SELECTION ============
const selectCategory = (category) => {
  form.category = category;
  if (category === 'MARKETING') {
    bodySuggestedVariables.value = ['customer_name', 'product_name', 'offer', 'code', 'period'];
    headerSuggestedVariables.value = ['header_title', 'offer_name', 'product'];
  } else if (category === 'UTILITY') {
    bodySuggestedVariables.value = ['customer_name', 'order_number', 'amount', 'date', 'time', 'status'];
    headerSuggestedVariables.value = ['header_title', 'order_id', 'user_name'];
  } else if (category === 'AUTHENTICATION') {
    bodySuggestedVariables.value = ['code', 'otp', 'user_name'];
    headerSuggestedVariables.value = [];
  }
  validateStep(1);
};

// ============ BODY VARIABLES ============
const updateBodyVariables = () => {
  const matches = form.body.match(/\{\{([^}]+)\}\}/g);
  const vars = matches ? matches.map((m) => m.replace(/\{\{|\}\}/g, '').trim()) : [];

  // Remove variables that no longer exist
  form.bodyVariables = form.bodyVariables.filter((v) => vars.includes(v));
  
  // Add new variables
  vars.forEach((v) => {
    if (!form.bodyVariables.includes(v)) {
      form.bodyVariables.push(v);
      if (!form.bodyVariableSamples[v]) {
        form.bodyVariableSamples[v] = '';
      }
    }
  });
};

const insertBodyVariable = (variable) => {
  const textarea = bodyTextarea.value;
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = form.body;
  const variableText = `{{${variable}}}`;

  form.body = text.substring(0, start) + variableText + text.substring(end);
  updateBodyVariables();

  nextTick(() => {
    textarea.focus();
    const newCursorPos = start + variableText.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
  });
};

const addCustomBodyVariable = () => {
  const name = prompt('Enter variable name (letters, numbers, underscores only):');
  if (name && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
    if (!form.bodyVariables.includes(name)) {
      form.bodyVariables.push(name);
      form.bodyVariableSamples[name] = '';
      if (!bodySuggestedVariables.value.includes(name)) {
        bodySuggestedVariables.value.push(name);
      }
    }
  } else if (name) {
    alert('Invalid variable name. Use only letters, numbers, and underscores.');
  }
};

const removeBodyVariable = (variable) => {
  form.bodyVariables = form.bodyVariables.filter((v) => v !== variable);
  delete form.bodyVariableSamples[variable];
  form.body = form.body.replace(new RegExp(`\\{\\{${variable}\\}\\}`, 'g'), '');
};

// ============ HEADER VARIABLES (Max 1) ============
const updateHeaderVariables = () => {
  const matches = form.headerText.match(/\{\{([^}]+)\}\}/g);
  const vars = matches ? matches.map((m) => m.replace(/\{\{|\}\}/g, '').trim()) : [];
  
  // Header can only have max 1 variable
  if (vars.length > 1) {
    useAlert('Header can only contain a maximum of 1 variable');
    // Keep only the first variable
    const firstVar = vars[0];
    // Remove all variables and add back only the first one
    form.headerText = form.headerText.replace(/\{\{[^}]+\}\}/g, '');
    // Add back the first variable at the position it was found
    // For simplicity, we'll append it at the end
    form.headerText = form.headerText + `{{${firstVar}}}`;
    form.headerVariables = [firstVar];
    if (!form.headerVariableSamples[firstVar]) {
      form.headerVariableSamples[firstVar] = '';
    }
    return;
  }

  // Update header variables
  form.headerVariables = vars;
  vars.forEach((v) => {
    if (!form.headerVariableSamples[v]) {
      form.headerVariableSamples[v] = '';
    }
  });
};

const insertHeaderVariable = (variable) => {
  // Check if header already has a variable
  if (form.headerVariables.length >= 1) {
    useAlert('Header can only contain a maximum of 1 variable');
    return;
  }

  const input = document.getElementById('header-text');
  if (!input) return;

  const start = input.selectionStart;
  const end = input.selectionEnd;
  const text = form.headerText;
  const variableText = `{{${variable}}}`;

  form.headerText = text.substring(0, start) + variableText + text.substring(end);
  updateHeaderVariables();

  nextTick(() => {
    input.focus();
    const newCursorPos = start + variableText.length;
    input.setSelectionRange(newCursorPos, newCursorPos);
  });
};

const addCustomHeaderVariable = () => {
  if (form.headerVariables.length >= 1) {
    useAlert('Header can only contain a maximum of 1 variable');
    return;
  }

  const name = prompt('Enter variable name (letters, numbers, underscores only):');
  if (name && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
    if (!form.headerVariables.includes(name)) {
      form.headerVariables.push(name);
      form.headerVariableSamples[name] = '';
      if (!headerSuggestedVariables.value.includes(name)) {
        headerSuggestedVariables.value.push(name);
      }
    }
  } else if (name) {
    alert('Invalid variable name. Use only letters, numbers, and underscores.');
  }
};

const removeHeaderVariable = (variable) => {
  form.headerVariables = form.headerVariables.filter((v) => v !== variable);
  delete form.headerVariableSamples[variable];
  form.headerText = form.headerText.replace(new RegExp(`\\{\\{${variable}\\}\\}`, 'g'), '');
};

const clearHeaderVariables = () => {
  form.headerVariables = [];
  form.headerVariableSamples = {};
};

// ============ RENDER PREVIEW ============
const renderPreviewBody = () => {
  let text = form.body;
  form.bodyVariables.forEach((v) => {
    const sample = form.bodyVariableSamples[v] || `[${v}]`;
    text = text.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), sample);
  });
  return text;
};

const renderPreviewHeader = () => {
  if (!form.headerText) return '';
  let text = form.headerText;
  form.headerVariables.forEach((v) => {
    const sample = form.headerVariableSamples[v] || `[${v}]`;
    text = text.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), sample);
  });
  return text;
};

// ============ FILE UPLOAD ============
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    form.headerFile = file;
    if (form.headerOption === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        form.headerPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      form.headerPreview = null;
    }
  }
};

const clearHeaderPreview = () => {
  form.headerPreview = null;
  form.headerFile = null;
};

// ============ MODAL CONTROLS ============
const resetForm = () => {
  Object.assign(form, {
    category: '',
    name: '',
    headerOption: '',
    headerText: '',
    headerFile: null,
    headerPreview: null,
    body: '',
    bodyVariables: [],
    bodyVariableSamples: {},
    headerVariables: [],
    headerVariableSamples: {},
    footer: '',
    buttons: { website: null, call: null, copyCode: null },
    wabaId: '',
    messageTtl: 60,
    codeExpiration: 5,
    copyCodeButtonText: 'Tap to copy',
  });
};

const openCreateModal = () => {
  isEditing.value = false;
  editingId.value = null;
  currentStep.value = 1;
  showStepErrors.value = false;
  resetForm();
  showModal.value = true;
  document.body.style.overflow = 'hidden';
};

const openEditModal = (template) => {
  isEditing.value = true;
  editingId.value = template.id;
  currentStep.value = 1;
  showStepErrors.value = false;

  // Parse template components
  const bodyComponent = getTemplateBody(template);
  const headerComponent = getTemplateHeader(template);
  const footerComponent = getTemplateFooter(template);
  const buttonsComponent = getTemplateButtons(template);

  // Extract body variables
  const bodyText = bodyComponent || '';
  const bodyMatches = bodyText.match(/\{\{([^}]+)\}\}/g);
  const bodyVars = bodyMatches ? bodyMatches.map((m) => m.replace(/\{\{|\}\}/g, '').trim()) : [];
  const bodyVariableSamples = {};
  bodyVars.forEach((v) => {
    bodyVariableSamples[v] = '';
  });

  // Extract header variables (max 1)
  const headerText = headerComponent?.text || '';
  const headerMatches = headerText.match(/\{\{([^}]+)\}\}/g);
  const headerVars = headerMatches ? headerMatches.map((m) => m.replace(/\{\{|\}\}/g, '').trim()) : [];
  const headerVariableSamples = {};
  headerVars.forEach((v) => {
    headerVariableSamples[v] = '';
  });

  // Handle buttons
  let buttons = { website: null, call: null, copyCode: null };
  if (buttonsComponent && buttonsComponent.buttons) {
    buttonsComponent.buttons.forEach(button => {
      if (button.type === 'website') {
        buttons.website = { text: button.text, url: button.url };
      } else if (button.type === 'call') {
        buttons.call = { phone: button.phone, country: button.country || 'KE' };
      } else if (button.type === 'copy_code') {
        buttons.copyCode = { code: button.code };
      }
    });
  }

  Object.assign(form, {
    category: template.category || '',
    name: template.name || '',
    headerOption: headerComponent?.format?.toLowerCase() || '',
    headerText: headerText,
    headerFile: null,
    headerPreview: null,
    body: bodyText,
    bodyVariables: bodyVars,
    bodyVariableSamples: bodyVariableSamples,
    headerVariables: headerVars,
    headerVariableSamples: headerVariableSamples,
    footer: footerComponent?.text || '',
    buttons: buttons,
    wabaId: template.waba_id || '',
    messageTtl: template.message_ttl || 60,
    codeExpiration: template.code_expiration || 5,
    copyCodeButtonText: template.copy_code_button_text || 'Tap to copy',
  });

  showModal.value = true;
  document.body.style.overflow = 'hidden';
};

const closeModal = () => {
  showModal.value = false;
  document.body.style.overflow = '';
};

const openPreviewModal = (template) => {
  previewData.value = template;
  showPreview.value = true;
  document.body.style.overflow = 'hidden';
};

const closePreview = () => {
  showPreview.value = false;
  previewData.value = null;
  document.body.style.overflow = '';
};

// ============ SAVE TEMPLATE WITH FORM DATA ============
const saveTemplate = async () => {
  if (!isFormValid.value) {
    useAlert('Please complete all required fields');
    return;
  }

  isSubmitting.value = true;

  try {
    // Prepare the template data
    const templateData = {
      templateType: form.category,
      templateName: form.name.toLowerCase().replace(/\s+/g, '_'),
      WabaId: props.inbox.provider_config?.business_account_id,
      MobileId: props.inbox.provider_config?.phone_number_id,
      ApiKey: props.inbox.provider_config?.api_key,
    };

    // Authentication template
    if (form.category === 'AUTHENTICATION') {
      templateData.message_send_ttl = form.messageTtl;
      templateData.code_expiration_minutes = form.codeExpiration;
      templateData.code_button_name = form.copyCodeButtonText;
    } else {
      // Header
      if (form.headerOption) {
        templateData.headerOption = form.headerOption;
        
        if (form.headerOption === 'text' && form.headerText) {
          templateData.headerText = form.headerText;
          // Add header variables if any (max 1)
          if (form.headerVariables.length > 0) {
            const headerVars = {};
            form.headerVariables.forEach((v) => {
              headerVars[v] = form.headerVariableSamples[v] || `Sample ${v}`;
            });
            templateData.HeaderVariables = headerVars;
          }
        } else if (['image', 'document', 'video'].includes(form.headerOption) && form.headerFile) {
          // We'll handle the file separately
          templateData.hasFile = true;
        }
      }

      // Body
      templateData.bodyText = form.body;

      // Body Variables
      if (form.bodyVariables.length) {
        const bodyVars = {};
        form.bodyVariables.forEach((v) => {
          bodyVars[v] = form.bodyVariableSamples[v] || `Sample ${v}`;
        });
        templateData.bodyVariables = bodyVars;
      }

      // Footer
      if (form.footer) templateData.footer = form.footer;

      // Buttons
      if (form.buttons.website) {
        templateData.websiteButton = {
          text: form.buttons.website.text,
          url: form.buttons.website.url,
        };
      }
      if (form.buttons.call) {
        templateData.callButton = {
          phone: form.buttons.call.phone,
          country: form.buttons.call.country,
        };
      }
      if (form.buttons.copyCode) {
        templateData.copyCodeButton = {
          text: form.buttons.copyCode.code,
        };
      }
    }

    // Create FormData if we have a file
    let payload = templateData;
    if (form.headerFile && ['image', 'document', 'video'].includes(form.headerOption)) {
      const formData = new FormData();
      
      // Append all template data as JSON
      formData.append('templateData', JSON.stringify(templateData));
      
      // Append the file
      formData.append('headerFile', form.headerFile);
      
      payload = formData;
    }

    // Use store action to save
    if (isEditing.value) {
      await store.dispatch('inboxes/updateWhatsAppTemplate', {
        inboxId: props.inbox.id,
        templateId: editingId.value,
        templateData: payload,
      });
      useAlert('Template updated successfully!');
    } else {
      await store.dispatch('inboxes/createWhatsAppTemplate', {
        inboxId: props.inbox.id,
        templateData: payload,
      });
      useAlert('Template created successfully!');
    }

    closeModal();
  } catch (err) {
    console.error('Error saving template:', err);
    useAlert(err?.message || 'Failed to save template. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
};

// ============ DELETE TEMPLATE ============
const deleteTemplate = async (template) => {
  if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) return;

  try {
    await store.dispatch('inboxes/deleteWhatsAppTemplate', {
      inboxId: props.inbox.id,
      templateId: template.id,
    });
    useAlert('Template deleted successfully');
  } catch (err) {
    console.error('Error deleting template:', err);
    useAlert('Failed to delete template');
  }
};

// ============ COMPUTED PROPERTIES ============
const isFormValid = computed(() => {
  if (!form.category || !form.name || !form.body) return false;
  if (form.category === 'AUTHENTICATION' && !form.copyCodeButtonText) return false;
  return true;
});

const getApprovedCount = computed(
  () => templates.value.filter(t => t.status === 'APPROVED').length
);

const getPendingCount = computed(
  () => templates.value.filter(t => t.status === 'PENDING').length
);

const getRejectedCount = computed(
  () => templates.value.filter(t => t.status === 'REJECTED').length
);

// ============ HELPERS ============
const getStatusColor = (status) => {
  const colors = {
    APPROVED:
      'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/30',
    PENDING:
      'bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800/30',
    REJECTED:
      'bg-gradient-to-r from-red-100 to-rose-100 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/30',
    DRAFT:
      'bg-gradient-to-r from-gray-100 to-slate-100 dark:from-gray-800/30 dark:to-slate-800/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700/30',
  };
  return colors[status] || colors.DRAFT;
};

const getStatusDotColor = (status) => {
  const colors = {
    APPROVED: 'bg-green-500',
    PENDING: 'bg-yellow-500',
    REJECTED: 'bg-red-500',
    DRAFT: 'bg-gray-500',
  };
  return colors[status] || colors.DRAFT;
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ============ ON MOUNTED ============
onMounted(() => {
  console.log('Templates from inbox:', props.inbox.message_templates);
});
</script>

<style scoped>
.surface {
  @apply rounded-xl border border-n-weak bg-white dark:bg-n-slate-2;
}

.kpi-card {
  @apply flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-white px-4 py-3 dark:bg-n-slate-2;
}

.kpi-icon {
  @apply flex size-8 shrink-0 items-center justify-center rounded-lg;
}

.template-row {
  @apply flex flex-col justify-between gap-4 px-5 py-5 transition-colors hover:bg-n-slate-1 sm:flex-row sm:items-center;
}

.template-avatar {
  @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-slate-2 text-xs font-semibold text-n-slate-11 dark:bg-n-slate-3;
}

.status-badge {
  @apply inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide;
}

.variable-chip {
  @apply inline-flex items-center rounded-md border border-n-blue-3 bg-n-blue-1 px-2 py-0.5 font-mono text-[10px] font-medium text-n-blue-9;
}

.variable-button {
  @apply inline-flex h-7 items-center rounded-md border border-n-blue-3 bg-white px-2 text-[10px] font-medium text-n-blue-9 transition hover:border-n-blue-5 hover:bg-n-blue-1 dark:bg-n-slate-2 disabled:opacity-50 disabled:cursor-not-allowed;
}

.variable-button-add {
  @apply gap-1 border-n-weak text-n-slate-11 hover:border-n-blue-4 hover:text-n-blue-9;
}

.row-action,
.icon-button {
  @apply inline-flex size-9 items-center justify-center rounded-lg text-n-slate-10 transition hover:bg-n-slate-2 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9/20 dark:hover:bg-n-slate-3;
}

.row-action-danger {
  @apply hover:bg-red-50 hover:text-red-600;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.step-item {
  @apply relative flex min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-n-slate-10 transition hover:bg-n-slate-1;
}

.step-number {
  @apply flex size-7 shrink-0 items-center justify-center rounded-full border border-n-weak bg-white text-[11px] font-semibold dark:bg-n-slate-3;
}

.step-item-active {
  @apply bg-n-blue-1 text-n-blue-9;
}

.step-item-active .step-number {
  @apply border-n-blue-9 bg-n-blue-9 text-white;
}

.step-item-complete {
  @apply text-green-600;
}

.step-item-complete .step-number {
  @apply border-green-300 bg-green-50 text-green-600;
}

.form-step {
  @apply mx-auto max-w-2xl;
}

.section-eyebrow {
  @apply text-[10px] font-semibold uppercase tracking-[0.12em] text-n-blue-9;
}

.section-title {
  @apply mt-1 text-lg font-semibold tracking-tight text-n-slate-12;
}

.section-description {
  @apply mt-1.5 max-w-xl text-sm leading-6 text-n-slate-11;
}

.field-group {
  @apply mb-6;
}

.field-label {
  @apply mb-2 block text-sm font-medium text-n-slate-12;
}

.field-label-small {
  @apply mb-1.5 block text-xs font-medium text-n-slate-11;
}

.required {
  @apply text-red-600;
}

.optional {
  @apply ml-1 text-[10px] font-normal text-n-slate-10;
}

.field-help {
  @apply mt-1.5 text-xs leading-5 text-n-slate-10;
}

.field-error {
  @apply mt-1.5 flex items-center gap-1 text-xs text-red-600;
}

.form-input,
.form-textarea {
  @apply w-full rounded-lg border border-n-weak bg-white px-3.5 text-sm text-n-slate-12 outline-none transition placeholder:text-n-slate-9 focus:border-n-blue-7 focus:ring-2 focus:ring-n-blue-9/10 dark:bg-n-slate-3;
}

.form-input {
  @apply h-10;
}

.form-textarea {
  @apply min-h-[170px] resize-y py-3 leading-6;
}

.form-input-error {
  @apply border-red-400 focus:border-red-400 focus:ring-red-500/10;
}

.category-card {
  @apply flex min-h-[82px] items-start gap-3 rounded-xl border border-n-weak bg-white p-3 text-left transition hover:border-n-blue-5 hover:bg-n-slate-1 dark:bg-n-slate-2;
}

.category-card-selected {
  @apply border-n-blue-7 bg-n-blue-1 ring-1 ring-n-blue-7/20;
}

.category-card-icon {
  @apply flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-slate-2 text-n-slate-10 dark:bg-n-slate-3;
}

.category-card-selected .category-card-icon {
  @apply bg-white text-n-blue-9 dark:bg-n-slate-2;
}

.option-button {
  @apply inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-n-weak bg-white px-3 text-xs font-medium text-n-slate-11 transition hover:border-n-blue-5 hover:text-n-blue-9 dark:bg-n-slate-2;
}

.option-button-selected {
  @apply border-n-blue-7 bg-n-blue-1 text-n-blue-9 ring-1 ring-n-blue-7/20;
}

.upload-zone {
  @apply flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-slate-1 p-4 transition hover:border-n-blue-5 hover:bg-n-blue-1;
}

.notice-box {
  @apply flex items-start gap-3 rounded-xl border border-n-weak bg-n-slate-1 p-4;
}

.notice-box-warning {
  @apply border-yellow-300 bg-yellow-50/40;
}

.notice-icon {
  @apply flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-n-blue-9 shadow-sm dark:bg-n-slate-2;
}

.action-card {
  @apply rounded-xl border border-n-weak bg-n-slate-1 p-4;
}

.action-card-header {
  @apply mb-3 flex items-center justify-between gap-3;
}

.action-icon {
  @apply flex size-8 items-center justify-center rounded-lg;
}

.secondary-action,
.secondary-button,
.primary-button {
  @apply inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-n-blue-9/20 disabled:cursor-not-allowed disabled:opacity-50;
}

.secondary-action,
.secondary-button {
  @apply border border-n-weak bg-white text-n-slate-11 hover:bg-n-slate-1 hover:text-n-slate-12 dark:bg-n-slate-2;
}

.secondary-action {
  @apply h-8 px-3 text-xs;
}

.primary-button {
  @apply bg-n-blue-9 text-white shadow-sm hover:bg-n-blue-10;
}

.preview-chat {
  background-image: radial-gradient(rgba(71, 85, 105, 0.08) 1px, transparent 1px);
  background-size: 12px 12px;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: #334155;
}

@media (prefers-reduced-motion: reduce) {
  * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>